import React, { useState, useContext, useMemo } from "react";
import axios from "axios";
import {
  Box,
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  Snackbar,
  Alert,
  Fab,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";

import { OrderContext } from "../../contexts/OrderContext";
import searchIcon from "../../assets/img/icons/search.png";

const CreateStoneMaster = () => {
  const [stone, setStone] = useState({ Stone_name: "", priority: 10 });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const { fetchStoreData, storeData } = useContext(OrderContext);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const mfr_id = localStorage.getItem("id");

  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const filteredStones = useMemo(() => {
    return (
      storeData.stone?.filter((stone) =>
        stone.Stone_name.toLowerCase().includes(searchTerm.toLowerCase())
      ) || []
    );
  }, [searchTerm, storeData.stone]);

  const handleChange = (e) => {
    setStone({ ...stone, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const requestData = {
        Stone_name: stone.Stone_name,
        priority: stone.priority || 10,
        mfr_id,
      };

      await axios.post(
        `${API_BASE_URL}/api/fetchorder/createstone`,
        requestData
      );
      fetchStoreData();
      setSnackbar({
        open: true,
        message: "Stone created successfully!",
        severity: "success",
      });
      setStone({ Stone_name: "", priority: 10 });
      setShowForm(false);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Error creating stone",
        severity: "error",
      });
    }

    setLoading(false);
  };

  return (
    <Box p={3} position="relative">
      {/* Search and List */}
      {!showForm && (
        <Box maxWidth={600} mx="auto">
          <Box
            width="100%"
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            gap="0.4rem"
            sx={{ p: 1, mb: 3 }}
          >
            {/* Search Input */}
            <div className="custom-search-box" style={{ width: "100%" }}>
              <img src={searchIcon} alt="Search" className="search-icon" />
              <input
                type="text"
                placeholder="Search Stone"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Create button if no match */}
            {filteredStones.length === 0 && (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                alignSelf="center"
                sx={{
                  width: "75%",
                  bgcolor: "#0D7377",
                  padding: "0.4rem 0",
                  borderRadius: 2,
                  cursor: "pointer",
                }}
                onClick={() => {
                  setStone({ Stone_name: searchTerm, priority: 10 });
                  setShowForm(true);
                }}
              >
                <Typography sx={{ fontSize: 16, color: "#fffffa" }}>
                  Create This Stone
                </Typography>
              </Box>
            )}
          </Box>

          {/* Stone List */}
          <Grid container spacing={2} mb={10}>
            {filteredStones.map((stoneItem) => (
              <Grid item xs={12} key={stoneItem.id}>
                <Card sx={{ p: 1, boxShadow: 1 }}>
                  <Typography fontSize="14px" fontWeight="bold">
                    {stoneItem.Stone_name}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
          {filteredStones?.length === 0 && (
            <Typography textAlign="center" color="gray" fontStyle="italic">
              No such stone was found...
            </Typography>
          )}
        </Box>
      )}

      {/* Form */}
      {showForm && (
        <Card sx={{ maxWidth: 600, mx: "auto" }}>
          <CardContent>
            <Typography
              variant="h5"
              fontWeight="bold"
              textAlign="center"
              mb={2}
              color="#0D7377"
            >
              Create Stone Master
            </Typography>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                {/* Stone Name */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Stone Name"
                    name="Stone_name"
                    value={stone.Stone_name}
                    onChange={handleChange}
                    required
                    sx={{
                      "& label": { color: "#64748B" },
                      "& label.Mui-focused": { color: "#0D7377" },
                      "& .MuiInputBase-input": { color: "#333" },
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: "#A0ABBB" },
                        "&:hover fieldset": { borderColor: "#A0ABBB" },
                        "&.Mui-focused fieldset": { borderColor: "#0D7377" },
                      },
                    }}
                  />
                </Grid>

                {/* Priority */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Priority"
                    name="priority"
                    value={stone.priority}
                    onChange={handleChange}
                    inputProps={{ min: 1 }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    fullWidth
                    type="submit"
                    variant="contained"
                    disabled={loading}
                    sx={{
                      backgroundColor: "#0D7377",
                      color: "#fff",
                      "&:hover": { backgroundColor: "#0A5C60" },
                    }}
                  >
                    {loading ? "Saving..." : "Save"}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Floating Button */}
      {showForm && (
        <Fab
          color="primary"
          onClick={() => setShowForm(false)}
          sx={{
            position: "fixed",
            bottom: 80,
            right: 24,
            bgcolor: "#0D7377",
            "&:hover": { bgcolor: "#0A5C60" },
          }}
        >
          <CloseIcon />
        </Fab>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        sx={{
          '&.MuiSnackbar-root': {
            bottom: '100px !important', // force override snackbar position
          }
        }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default CreateStoneMaster;
